import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'hooks/useWindowSize';
import { desktopBreakpoint } from 'breakpoints';
import {
  StyledInfoSection,
  StyledTextArticle,
  StyledArticleHeadline,
  StyledArticleDescription,
  StyledArticleRelease,
  StyledIframeContainer,
  StyledIframe,
  StyledViewMore,
} from './styled';

interface CollectionInfoProps {
  aboutCollection: string,
  additionalDescription?: { header: string, text: string }[],
  promoVideoLink?: string
  releaseDate?: string,
  descriptionAlignment?: 'left' | 'center',
}

const CollectionInfo: FC<CollectionInfoProps> = ({
  aboutCollection,
  additionalDescription,
  promoVideoLink,
  releaseDate,
  descriptionAlignment,
}) => {
  const { t } = useTranslation();
  const [viewMore, setViewMore] = useState(false);
  const width = useWindowSize();
  const isMobile = width <= desktopBreakpoint;

  return (
    <StyledInfoSection data-test-id="info_section">
      {additionalDescription?.map(({ header, text }) => (
        <StyledTextArticle>
          <StyledArticleHeadline
            as="h3"
            font="secondary"
            textColor="primary"
            size="header2"
            lSpacing={2}
            uppercase
          >
            {header}
          </StyledArticleHeadline>
          <StyledArticleDescription
            textColor="textWhite"
            size="p"
            align={descriptionAlignment}
          >
            {text}
          </StyledArticleDescription>
        </StyledTextArticle>
      ))}
      {aboutCollection && (
        <StyledTextArticle>
          <StyledArticleHeadline
            data-test-id="article_headline"
            as="h3"
            font="secondary"
            textColor="primary"
            size="header2"
            lSpacing={2}
            uppercase
          >
            {t('collection.about')}
          </StyledArticleHeadline>
          <StyledArticleDescription
            data-test-id="article_description"
            data-length-attribute={`${aboutCollection.length}`}
            textColor="textWhite"
            size="p"
            align={descriptionAlignment}
          >
            <span>
              {viewMore || aboutCollection.length < (isMobile ? 200 : 355)
                ? aboutCollection : `${aboutCollection.slice(0, isMobile ? 200 : 355).trimEnd()}... `}
            </span>
            {(aboutCollection.length > (isMobile ? 200 : 355)) && (
              <StyledViewMore
                data-test-id="article_view_more"
                onClick={() => setViewMore(true)}
                $hide={viewMore}
              >
                {t('common.viewMore')}
              </StyledViewMore>
            )}
          </StyledArticleDescription>
        </StyledTextArticle>
      )}
      {releaseDate && (
        <StyledArticleRelease
          data-test-id="article_release"
          textColor="textWhite"
          size="p"
          lSpacing={1.1}
          bold
        >
          {t('collection.releaseDate', { releaseDate })}
        </StyledArticleRelease>
      )}
      {promoVideoLink && (
        <StyledIframeContainer>
          <StyledIframe
            data-test-id="frame"
            src={promoVideoLink}
            frameBorder="none"
            allowFullScreen
          />
        </StyledIframeContainer>
      )}
    </StyledInfoSection>
  );
};

export default CollectionInfo;
